import {
  useEffect,
  useState
} from 'react';

import {
  CapacitorHttp
} from '@capacitor/core';

import {
  isNativeApp
} from '../../../utils/nativeApp';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const API_URL =
  process.env.REACT_APP_API_URL;


function apiHeaders() {
  return {
    'apikey': SUPABASE_KEY,
    'Authorization':
      'Bearer ' + SUPABASE_KEY
  };
}


async function fetchCourseInstructorIds(course) {
  const ids = [];

  if (course?.instructor_id) {
    ids.push(String(course.instructor_id));
  }

  if (!course?.id) {
    return ids;
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/course_access?course_id=eq.${course.id}&select=instructor_id`,
    {
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      `Loading course instructors failed (${response.status}): ${detail}`
    );
  }

  const rows =
    await response.json();

  (Array.isArray(rows) ? rows : [])
    .forEach(row => {
      if (row?.instructor_id) {
        ids.push(
          String(row.instructor_id)
        );
      }
    });

  return [...new Set(ids)];
}


async function fetchCourseInstructors(course) {
  const instructorIds =
    await fetchCourseInstructorIds(course);

  if (!instructorIds.length) {
    return [];
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?id=in.(${instructorIds.join(',')})&select=*`,
    {
      headers: apiHeaders()
    }
  );

  if (!response.ok) {
    const detail =
      await response.text();

    throw new Error(
      `Loading instructor information failed (${response.status}): ${detail}`
    );
  }

  const rows =
    await response.json();

  const instructors =
    Array.isArray(rows)
      ? rows
      : [];

  const primaryId =
    course?.instructor_id
      ? String(course.instructor_id)
      : null;

  return instructors.sort((a, b) => {
    const aPrimary =
      String(a.id) === primaryId;

    const bPrimary =
      String(b.id) === primaryId;

    if (aPrimary && !bPrimary) {
      return -1;
    }

    if (!aPrimary && bPrimary) {
      return 1;
    }

    return String(
      a.name || a.email || ''
    ).localeCompare(
      String(
        b.name || b.email || ''
      ),
      undefined,
      {
        sensitivity: 'base'
      }
    );
  });
}


async function fetchNativeInstructorPhoto(
  instructorId
) {
  if (
    !instructorId ||
    !API_URL
  ) {
    return null;
  }

  const response =
    await CapacitorHttp.post({
      url: API_URL,
      headers: {
        'Content-Type':
          'application/json'
      },
      data: {
        action:
          'getInstructorImageData',
        instructorId
      }
    });

  if (
    response.status < 200 ||
    response.status >= 300
  ) {
    throw new Error(
      `Instructor photo request failed (${response.status}).`
    );
  }

  const data =
    typeof response.data === 'string'
      ? JSON.parse(response.data)
      : response.data;

  if (!data?.ok) {
    throw new Error(
      data?.error ||
        'Instructor photo request failed.'
    );
  }

  if (
    !data.hasImage ||
    !data.base64Data
  ) {
    return null;
  }

  return (
    `data:${
      data.mimeType ||
      'image/jpeg'
    };base64,` +
    data.base64Data
  );
}


export default function StudentInstructor({
  course
}) {
  const [
    instructors,
    setInstructors
  ] = useState([]);

  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    error,
    setError
  ] = useState('');

  const [
    photoSources,
    setPhotoSources
  ] = useState({});


    useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setError('');
    setInstructors([]);
    setPhotoSources({});

    fetchCourseInstructors({
      id: course?.id,
      instructor_id: course?.instructor_id
    })
      .then(result => {
        if (!cancelled) {
          setInstructors(result);
        }
      })
      .catch(err => {
        console.error(
          'Instructor load failed:',
          err
        );

        if (!cancelled) {
          setError(
            err?.message ||
              'Failed to load instructor information.'
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    course?.id,
    course?.instructor_id
  ]);


  useEffect(() => {
    let cancelled = false;

    if (!instructors.length) {
      setPhotoSources({});

      return () => {
        cancelled = true;
      };
    }

    if (!isNativeApp()) {
      const next = {};

      instructors.forEach(
        instructor => {
          if (
            instructor
              .photo_drive_file_id
          ) {
            next[instructor.id] =
              `https://lh3.googleusercontent.com/d/${instructor.photo_drive_file_id}`;
          }
        }
      );

      setPhotoSources(next);

      return () => {
        cancelled = true;
      };
    }

    async function loadPhotos() {
      const entries =
        await Promise.all(
          instructors.map(
            async instructor => {
              if (
                !instructor
                  .photo_drive_file_id
              ) {
                return [
                  instructor.id,
                  null
                ];
              }

              try {
                const src =
                  await fetchNativeInstructorPhoto(
                    instructor.id
                  );

                return [
                  instructor.id,
                  src
                ];
              } catch (err) {
                console.error(
                  `Instructor photo load failed for ${instructor.id}:`,
                  err
                );

                return [
                  instructor.id,
                  null
                ];
              }
            }
          )
        );

      if (cancelled) {
        return;
      }

      const next = {};

      entries.forEach(
        ([id, src]) => {
          if (src) {
            next[id] = src;
          }
        }
      );

      setPhotoSources(next);
    }

    loadPhotos();

    return () => {
      cancelled = true;
    };
  }, [instructors]);


  function handlePhotoError(
    instructorId
  ) {
    setPhotoSources(prev => {
      const next = {
        ...prev
      };

      delete next[instructorId];

      return next;
    });
  }


  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.muted}>
          Loading...
        </div>
      </div>
    );
  }


  return (
    <div style={styles.wrap}>
      <div style={styles.content}>
        <h2 style={styles.heading}>
          Instructor Information
        </h2>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {!error &&
          instructors.length === 0 && (
            <div style={styles.muted}>
              No instructors are assigned
              to this course.
            </div>
          )}

        {!error &&
          instructors.map(
            instructor => {
              const photoSrc =
                photoSources[
                  instructor.id
                ] || null;

              return (
                <div
                  key={instructor.id}
                  style={styles.card}
                >
                  <div
                    style={
                      styles.cardInner
                    }
                  >
                    <div
                      style={
                        styles.photoWrap
                      }
                    >
                      {photoSrc ? (
                        <img
                          src={photoSrc}
                          alt={
                            instructor.name ||
                            'Instructor'
                          }
                          style={
                            styles.photo
                          }
                          onError={() =>
                            handlePhotoError(
                              instructor.id
                            )
                          }
                        />
                      ) : (
                        <div
                          style={
                            styles.photoPlaceholder
                          }
                        >
                          {(
                            instructor.name ||
                            'I'
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </div>
                      )}
                    </div>

                    <div
                      style={styles.info}
                    >
                      <div
                        style={styles.name}
                      >
                        {instructor.name ||
                          'Instructor'}
                      </div>

                      {instructor.email && (
                        <div
                          style={
                            styles.infoRow
                          }
                        >
                          <span
                            style={
                              styles.infoLabel
                            }
                          >
                            Email
                          </span>

                          <a
                            href={`mailto:${instructor.email}`}
                            style={
                              styles.emailLink
                            }
                          >
                            {
                              instructor.email
                            }
                          </a>
                        </div>
                      )}

                      {instructor.office && (
                        <div
                          style={
                            styles.infoRow
                          }
                        >
                          <span
                            style={
                              styles.infoLabel
                            }
                          >
                            Office
                          </span>

                          <span
                            style={
                              styles.infoText
                            }
                          >
                            {
                              instructor.office
                            }
                          </span>
                        </div>
                      )}

                      {instructor
                        .office_hours && (
                        <div
                          style={
                            styles.infoRow
                          }
                        >
                          <span
                            style={
                              styles.infoLabel
                            }
                          >
                            Hours
                          </span>

                          <span
                            style={
                              styles.infoText
                            }
                          >
                            {
                              instructor
                                .office_hours
                            }
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            }
          )}
      </div>
    </div>
  );
}


const styles = {
  wrap: {
    minHeight: 0,
    background: 'transparent'
  },

  content: {
    padding: 0,
    maxWidth: 'none',
    margin: 0
  },

  muted: {
    color: '#627d98',
    fontSize: '14px'
  },

  error: {
    background: '#faeaea',
    border:
      '1px solid #e8bcbc',
    borderRadius: '6px',
    padding: '10px 12px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '12px'
  },

  heading: {
    margin: '0 0 20px',
    fontSize: '26px',
    color: '#243b53',
    fontWeight: '700'
  },

  card: {
    background: 'white',
    border:
      '1px solid #d9e2ec',
    borderRadius: '6px',
    padding: '20px',
    marginBottom: '12px',
    boxShadow: 'none'
  },

  cardInner: {
    display: 'flex',
    gap: '20px',
    alignItems: 'flex-start'
  },

  photoWrap: {
    width: '80px',
    height: '80px',
    borderRadius: '6px',
    overflow: 'hidden',
    border:
      '1px solid #d9e2ec',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#edf1f5'
  },

  photo: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },

  photoPlaceholder: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#102a43',
    color: 'white',
    fontSize: '24px',
    fontWeight: '700'
  },

  info: {
    flex: 1,
    minWidth: 0
  },

  name: {
    fontWeight: '700',
    fontSize: '18px',
    color: '#243b53',
    marginBottom: '10px'
  },

  infoRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '6px',
    minWidth: 0
  },

  infoLabel: {
    width: '46px',
    flexShrink: 0,
    color: '#829ab1',
    fontSize: '9px',
    fontWeight: '800',
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    paddingTop: '3px'
  },

  infoText: {
    fontSize: '14px',
    color: '#243b53',
    lineHeight: '1.5'
  },

  emailLink: {
    fontSize: '14px',
    color: '#2f6f9f',
    textDecoration: 'underline',
    minWidth: 0,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word'
  }
};